import React, { useMemo, useState } from 'react';
import { CalendarDays, Clock, Newspaper } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { items as blogItems, topics as blogTopics } from '../config/blog';
import LanguageSwitcher from '../components/LanguageSwitcher';

const PageHeader = ({ t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
          }}
        />
      </a>
      <Link
        to="/blog"
        className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
      >
        {t('blog_index.back_to_blog')}
      </Link>
      <LanguageSwitcher variant="compact" />
    </div>
  </header>
);

const BlogHPage = () => {
  const { t, i18n } = useTranslation();
  const [activeTopic, setActiveTopic] = useState(null);
  const localeMap = { es: 'es-ES', en: 'en-US', pt: 'pt-PT' };
  const locale = localeMap[i18n.resolvedLanguage] || localeMap.es;

  const today = new Date();
  const dateString = today.toLocaleDateString(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timeString = today.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

  const localizedTopics = useMemo(() => {
    const translated = t('blog_content.topics', { returnObjects: true });
    if (!Array.isArray(translated) || translated.length !== blogTopics.length) return blogTopics;
    return translated;
  }, [t]);

  const topicLabelByRaw = useMemo(() => {
    const map = new Map();
    blogTopics.forEach((rawTopic, index) => {
      map.set(rawTopic, localizedTopics[index] || rawTopic);
    });
    return map;
  }, [localizedTopics]);

  const getTopicLabel = (rawTopic) => topicLabelByRaw.get(rawTopic) || rawTopic;

  const localizedItems = useMemo(() => {
    const translated = t('blog_content.items', { returnObjects: true });
    if (!Array.isArray(translated)) return blogItems;

    const translatedByRoute = new Map(
      translated
        .filter((entry) => entry && typeof entry.route === 'string')
        .map((entry) => [entry.route, entry])
    );

    return blogItems.map((item) => {
      const itemTranslation = translatedByRoute.get(item.route);
      if (!itemTranslation) return item;
      return {
        ...item,
        title: itemTranslation.title || item.title,
        subtitle: itemTranslation.subtitle || item.subtitle
      };
    });
  }, [t]);

  const topicsToRender = useMemo(() => {
    if (!activeTopic) return blogTopics;
    return [activeTopic];
  }, [activeTopic]);

  const itemsByTopic = useMemo(() => {
    const map = new Map();
    blogTopics.forEach((t) => map.set(t, []));
    localizedItems.forEach((item) => {
      (item.topics || []).forEach((topicRaw) => {
        if (!map.has(topicRaw)) map.set(topicRaw, []);
        map.get(topicRaw).push(item);
      });
    });
    return map;
  }, [localizedItems]);

  return (
    <div className="min-h-screen bg-[#f7f7f5]">
      <PageHeader t={t} />
      <main className="pt-20">
        <section className="border-y border-gray-300 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between py-3 text-gray-700">
              <div className="flex items-center gap-2">
                <Newspaper className="w-5 h-5" />
                <span className="uppercase tracking-widest text-xs">{t('blog_index.editorial_index')}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />{dateString}</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{timeString}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight text-[#141414] text-center">
            {t('blog_index.title')}
          </h1>
          <p className="mt-4 text-center text-gray-700 max-w-3xl mx-auto">
            {t('blog_index.subtitle')}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setActiveTopic(null)}
              className={`px-3 py-1 text-xs rounded border ${
                !activeTopic ? 'bg-[#141414] text-white border-[#141414]' : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              {t('blog_index.all')}
            </button>
            {blogTopics.map((topicRaw) => (
              <button
                key={topicRaw}
                onClick={() => setActiveTopic(activeTopic === topicRaw ? null : topicRaw)}
                className={`px-3 py-1 text-xs rounded border ${
                  activeTopic === topicRaw ? 'bg-[#141414] text-white border-[#141414]' : 'bg-white text-gray-700 border-gray-300'
                }`}
              >
                {getTopicLabel(topicRaw)}
              </button>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
          <div className="space-y-10">
            {topicsToRender.map((topic) => {
              const list = itemsByTopic.get(topic) || [];
              if (list.length === 0) return null;
              return (
                <div key={topic} className="bg-white border border-gray-200">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#141414]">{getTopicLabel(topic)}</h2>
                    <div className="text-xs font-semibold text-gray-500">{list.length} {t('blog_index.posts_count')}</div>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {list.map((it) => (
                      <article key={it.route} className="p-6">
                        <div className="md:flex md:items-stretch gap-5">
                          <Link
                            to={it.route}
                            className="relative block overflow-hidden bg-gray-100 border border-gray-200 h-36 md:h-auto md:w-3/12 md:self-stretch"
                          >
                            <img
                              src={it.image}
                              alt={it.title}
                              className="absolute inset-0 w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://placehold.co/1200x675/f0f0f0/171717?text=Imagen';
                              }}
                            />
                          </Link>
                          <div className="mt-5 md:mt-0 md:w-9/12">
                            <div className="text-xs font-semibold text-gray-600 uppercase tracking-widest">{it.title}</div>
                            <h3 className="mt-2 font-serif text-xl md:text-2xl font-bold text-[#141414] leading-snug">
                              <Link to={it.route}>{it.title}</Link>
                            </h3>
                            <p className="mt-2 text-gray-700">{it.subtitle}</p>
                            <div className="mt-4 flex flex-wrap gap-2">
                              {(it.topics || []).map((topicRaw) => (
                                <span key={topicRaw} className="text-xs font-semibold bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                  {getTopicLabel(topicRaw)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
};

export default BlogHPage;
